import { useEffect, useRef, useState, type SVGProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * BotanicalAccent — décorations SVG botaniques warm wellness.
 *
 * 4 variants organiques (pas symétriques, pas clipart) à placer en arrière
 * d'éléments pour ajouter une signature visuelle warm wellness (boutique
 * éco-conscious premium, NOT magazine).
 *
 * Toutes les variants sont décoratives — `aria-hidden`, `pointer-events-none`,
 * z-0 par défaut. Le parent doit être `relative overflow-hidden` pour scoper.
 *
 * `currentColor` ne suffit pas car chaque variant utilise une couleur dédiée
 * de la palette (sage / terracotta / peach) — la fill est encodée dans le path.
 */
export type BotanicalVariant =
  | 'leaf-sage'
  | 'tendril-terracotta'
  | 'bouquet-mixed'
  | 'sprig-peach'
  | 'rose-tendril';

export type BotanicalPosition =
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'center';

export type BotanicalSize = 'sm' | 'md' | 'lg';

interface BotanicalAccentProps {
  variant: BotanicalVariant;
  position?: BotanicalPosition;
  size?: BotanicalSize;
  className?: string;
  /** 0.0 - 1.0, default 0.15 — restera derrière contenu (z-0). */
  opacity?: number;
}

const SIZE_MAP: Record<BotanicalSize, string> = {
  sm: 'w-20 h-20',   // 80px
  md: 'w-36 h-36',   // 144px
  lg: 'w-56 h-56'    // 224px
};

const POSITION_MAP: Record<BotanicalPosition, string> = {
  'top-left': 'absolute top-0 left-0 -translate-x-4 -translate-y-4',
  'top-right': 'absolute top-0 right-0 translate-x-4 -translate-y-4',
  'bottom-left': 'absolute bottom-0 left-0 -translate-x-4 translate-y-4',
  'bottom-right': 'absolute bottom-0 right-0 translate-x-4 translate-y-4',
  'center': 'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
};

/* Origin transform per position — botanical "pousse" depuis le bord ancré
   (top-left = origine bottom-right, etc.) pour effet "grow into the section". */
const ORIGIN_MAP: Record<BotanicalPosition, string> = {
  'top-left': 'origin-bottom-right',
  'top-right': 'origin-bottom-left',
  'bottom-left': 'origin-top-right',
  'bottom-right': 'origin-top-left',
  'center': 'origin-center'
};

export function BotanicalAccent({
  variant,
  position = 'top-left',
  size = 'md',
  className,
  opacity = 0.22
}: BotanicalAccentProps) {
  const wrapperClass = cn(
    SIZE_MAP[size],
    POSITION_MAP[position],
    'pointer-events-none z-0',
    `botanical-${variant}`,
    className
  );

  const ref = useRef<HTMLDivElement>(null);
  // Phase 27.72D — lazy init : reduced-motion sautent l'observer, revealed direct.
  const [revealed, setRevealed] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (revealed) return; // déjà revealed via reduced-motion lazy init
    if (typeof window === 'undefined') return;
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true);
            observer.disconnect();
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [revealed]);

  return (
    <div
      ref={ref}
      className={wrapperClass}
      style={{ opacity }}
      aria-hidden="true"
      data-variant={variant}
    >
      {/* 2 nested wrappers : extérieur = scroll-draw grow-in (scale + blur, transition
         déclenchée par IO), intérieur = breathing (keyframes scale+rotate slow loop).
         Séparation évite collision transform property. Wrapper externe ne touche pas
         à l'opacity (préservée sur le div racine pour les tests). */}
      <div
        className={cn(
          'botanical-grow h-full w-full',
          ORIGIN_MAP[position],
          revealed && 'botanical-grown'
        )}
      >
        <div className="botanical-breathe h-full w-full">
          {variant === 'leaf-sage' && <LeafSageSvg />}
          {variant === 'tendril-terracotta' && <TendrilTerracottaSvg />}
          {variant === 'bouquet-mixed' && <BouquetMixedSvg />}
          {variant === 'sprig-peach' && <SprigPeachSvg />}
          {variant === 'rose-tendril' && <RoseTendrilSvg />}
        </div>
      </div>
    </div>
  );
}

/* === Variants SVG ===
   Chaque variant utilise des couleurs OKLCH inline (pas currentColor) pour
   préserver la palette warm wellness peu importe le contexte parent. */

const svgBase: SVGProps<SVGSVGElement> = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 200 200',
  fill: 'none',
  className: 'h-full w-full'
};

/** leaf-sage — 2 feuilles sage stylisées avec branche organique. */
function LeafSageSvg() {
  return (
    <svg {...svgBase}>
      {/* Branche centrale courbe */}
      <path
        d="M 100 180 Q 95 130, 105 90 Q 115 55, 100 25"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Grande feuille gauche — organique, pointe vers haut-gauche */}
      <path
        d="M 100 110 Q 60 95, 45 65 Q 55 50, 80 60 Q 95 80, 100 110 Z"
        fill="oklch(0.69 0.055 122 / 0.6)"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="1"
      />
      {/* Nervure feuille gauche */}
      <path
        d="M 100 110 Q 75 90, 55 65"
        stroke="oklch(0.69 0.055 122 / 0.5)"
        strokeWidth="0.75"
        fill="none"
      />
      {/* Petite feuille droite — pointe vers haut-droite, asymétrique */}
      <path
        d="M 105 140 Q 130 130, 145 105 Q 140 95, 120 105 Q 110 120, 105 140 Z"
        fill="oklch(0.69 0.055 122 / 0.45)"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="1"
      />
      {/* Nervure feuille droite */}
      <path
        d="M 105 140 Q 125 125, 142 108"
        stroke="oklch(0.69 0.055 122 / 0.5)"
        strokeWidth="0.75"
        fill="none"
      />
      {/* 3e mini-feuille tout en haut, asymétrique */}
      <path
        d="M 100 35 Q 115 28, 122 18 Q 117 12, 105 22 Q 100 30, 100 35 Z"
        fill="oklch(0.69 0.055 122 / 0.4)"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="0.75"
      />
    </svg>
  );
}

/** tendril-terracotta — vrille organique 3 spirales asymétriques. */
function TendrilTerracottaSvg() {
  return (
    <svg {...svgBase}>
      {/* Branche principale qui descend et se vrille */}
      <path
        d="M 30 30 Q 50 50, 70 70 Q 90 95, 100 120 Q 105 145, 95 165"
        stroke="oklch(0.62 0.135 38)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Spirale 1 (haut, petite) */}
      <path
        d="M 105 75 Q 115 70, 118 80 Q 120 92, 110 95 Q 100 95, 100 85"
        stroke="oklch(0.62 0.135 38)"
        strokeWidth="1.25"
        strokeLinecap="round"
        fill="none"
      />
      {/* Spirale 2 (milieu, moyenne) */}
      <path
        d="M 140 110 Q 155 105, 158 120 Q 160 138, 145 142 Q 130 142, 128 128 Q 128 117, 138 115"
        stroke="oklch(0.62 0.135 38)"
        strokeWidth="1.25"
        strokeLinecap="round"
        fill="none"
      />
      {/* Spirale 3 (bas, plus serrée) */}
      <path
        d="M 60 140 Q 45 145, 48 158 Q 53 170, 68 168 Q 80 165, 80 153 Q 78 145, 70 145 Q 65 147, 65 153"
        stroke="oklch(0.62 0.135 38)"
        strokeWidth="1.25"
        strokeLinecap="round"
        fill="none"
      />
      {/* Petits points décoratifs (graines) */}
      <circle cx="125" cy="60" r="2" fill="oklch(0.62 0.135 38 / 0.7)" />
      <circle cx="40" cy="100" r="1.5" fill="oklch(0.62 0.135 38 / 0.6)" />
    </svg>
  );
}

/** bouquet-mixed — 3 feuilles + 1 fleur terracotta + 1 baie peach. */
function BouquetMixedSvg() {
  return (
    <svg {...svgBase}>
      {/* Tige centrale */}
      <path
        d="M 100 180 Q 100 130, 95 80 Q 92 50, 100 30"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Feuille sage gauche bas */}
      <path
        d="M 96 150 Q 70 145, 55 125 Q 60 115, 80 122 Q 92 135, 96 150 Z"
        fill="oklch(0.69 0.055 122 / 0.5)"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="0.75"
      />
      {/* Feuille sage droite milieu */}
      <path
        d="M 96 115 Q 122 110, 138 92 Q 132 82, 115 92 Q 100 105, 96 115 Z"
        fill="oklch(0.69 0.055 122 / 0.45)"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="0.75"
      />
      {/* Feuille sage gauche haut, petite */}
      <path
        d="M 94 80 Q 75 78, 65 65 Q 70 58, 82 65 Q 92 73, 94 80 Z"
        fill="oklch(0.69 0.055 122 / 0.4)"
        stroke="oklch(0.69 0.055 122)"
        strokeWidth="0.75"
      />
      {/* Fleur terracotta en haut, asymétrique 5 pétales */}
      <g transform="translate(100 35)">
        {/* Pétales */}
        <ellipse cx="-12" cy="-3" rx="9" ry="5" fill="oklch(0.62 0.135 38 / 0.6)" transform="rotate(-30)" />
        <ellipse cx="3" cy="-12" rx="9" ry="5" fill="oklch(0.62 0.135 38 / 0.55)" transform="rotate(20)" />
        <ellipse cx="13" cy="0" rx="8" ry="5" fill="oklch(0.62 0.135 38 / 0.65)" transform="rotate(60)" />
        <ellipse cx="-3" cy="10" rx="8" ry="4" fill="oklch(0.62 0.135 38 / 0.5)" transform="rotate(110)" />
        {/* Centre fleur */}
        <circle cx="0" cy="0" r="3" fill="oklch(0.78 0.085 38)" />
      </g>
      {/* Baie peach en bas droite */}
      <circle cx="125" cy="140" r="6" fill="oklch(0.78 0.085 38 / 0.7)" stroke="oklch(0.62 0.135 38 / 0.4)" strokeWidth="0.75" />
      {/* Petit pédoncule baie */}
      <path d="M 125 134 Q 122 128, 118 124" stroke="oklch(0.69 0.055 122)" strokeWidth="0.75" fill="none" />
    </svg>
  );
}

/** sprig-peach — petite branche minimaliste 3 feuilles peach. */
function SprigPeachSvg() {
  return (
    <svg {...svgBase}>
      {/* Tige principale */}
      <path
        d="M 40 160 Q 70 130, 95 100 Q 120 75, 160 50"
        stroke="oklch(0.78 0.085 38)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Feuille 1 — bas, plus grande, pointe vers bas-droite */}
      <path
        d="M 70 130 Q 95 138, 105 158 Q 95 168, 78 160 Q 68 145, 70 130 Z"
        fill="oklch(0.78 0.085 38 / 0.55)"
        stroke="oklch(0.62 0.135 38 / 0.5)"
        strokeWidth="0.75"
      />
      <path d="M 70 130 Q 85 145, 102 162" stroke="oklch(0.62 0.135 38 / 0.4)" strokeWidth="0.5" fill="none" />
      {/* Feuille 2 — milieu, pointe vers haut-gauche */}
      <path
        d="M 105 100 Q 85 95, 70 80 Q 75 70, 92 78 Q 102 90, 105 100 Z"
        fill="oklch(0.78 0.085 38 / 0.45)"
        stroke="oklch(0.62 0.135 38 / 0.5)"
        strokeWidth="0.75"
      />
      {/* Feuille 3 — haut, plus petite, pointe vers haut-droite */}
      <path
        d="M 135 70 Q 152 72, 162 60 Q 158 52, 145 58 Q 137 65, 135 70 Z"
        fill="oklch(0.78 0.085 38 / 0.5)"
        stroke="oklch(0.62 0.135 38 / 0.5)"
        strokeWidth="0.75"
      />
      {/* Petite baie peach pointe */}
      <circle cx="160" cy="48" r="3" fill="oklch(0.62 0.135 38 / 0.5)" />
    </svg>
  );
}

/** rose-tendril — Phase 27.17 — vrille rose organique 2 spirales asymétriques + baies. */
function RoseTendrilSvg() {
  return (
    <svg {...svgBase}>
      {/* Branche principale qui descend et se vrille */}
      <path
        d="M 35 25 Q 55 50, 80 75 Q 105 100, 110 130 Q 110 160, 95 175"
        stroke="oklch(0.78 0.045 5)"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Spirale 1 (haut) */}
      <path
        d="M 95 60 Q 110 55, 115 70 Q 117 85, 105 90 Q 92 90, 90 78"
        stroke="oklch(0.78 0.045 5)"
        strokeWidth="1.25"
        strokeLinecap="round"
        fill="none"
      />
      {/* Spirale 2 (bas) */}
      <path
        d="M 125 110 Q 145 105, 150 122 Q 152 142, 135 145 Q 118 145, 116 130"
        stroke="oklch(0.78 0.045 5)"
        strokeWidth="1.25"
        strokeLinecap="round"
        fill="none"
      />
      {/* Petites baies rose-soft */}
      <circle cx="60" cy="50" r="3" fill="oklch(0.88 0.045 5)" stroke="oklch(0.78 0.045 5 / 0.6)" strokeWidth="0.5" />
      <circle cx="125" cy="160" r="2.5" fill="oklch(0.88 0.045 5)" stroke="oklch(0.78 0.045 5 / 0.6)" strokeWidth="0.5" />
      <circle cx="155" cy="135" r="2" fill="oklch(0.94 0.025 5)" stroke="oklch(0.78 0.045 5 / 0.5)" strokeWidth="0.5" />
      {/* Petite feuille rose-dusty */}
      <path
        d="M 80 90 Q 95 95, 100 110 Q 90 115, 75 105 Q 72 95, 80 90 Z"
        fill="oklch(0.78 0.045 5 / 0.4)"
        stroke="oklch(0.78 0.045 5 / 0.6)"
        strokeWidth="0.5"
      />
    </svg>
  );
}
